<template>
	<div class="item-table">
		<div class="flex-table" role="table-head">
			<div class="flex-table-row" role="row-group">
				<div class="flex-table-cell" role="column-header">Line</div>
				<div class="flex-table-cell" role="column-header">SKU #</div>
				<div class="flex-table-cell" role="column-header">Description</div>
				<div class="flex-table-cell" role="column-header">Quantity</div>
				<div class="flex-table-cell" role="column-header">Colors</div>
				<div class="flex-table-cell" role="column-header">Size</div>
				<div class="flex-table-cell" role="column-header">Vendor Cost</div>
				<div class="flex-table-cell" role="column-header">Total</div>
				<div class="flex-table-cell" role="column-header">&nbsp;</div>
			</div>
		</div>
		<div
			v-for="(item, itemIndex) in localTableData"
			:key="itemIndex"
			class="flex-table"
			role="table-body"
		>
			<!-- main inventory searched item -->
			<div class="flex-table-row" role="row-group">
				<div class="flex-table-cell" role="cell">
					<span class="item-table-line-number">{{ renderLineNumber(item) }}</span>
				</div>
				<div class="flex-table-cell" role="cell">
					<SystemCategoryIcon :systemCategoryId="item.systemCategoryId" />
					<div class="form-input search-input" :data-line="renderLineNumber(item)">
						<v-text-field
							prepend-inner-icon="mdi-magnify"
							outlined
							dense
							hide-details="auto"
							v-model="item.styleCode"
							clearable
							@keydown="(e) => keyboardShortcuts(item, e, itemIndex)"
							@keyup="searchItems(item)"
							@focus="focusedItemIndex = -1"
							@blur="hideItemSearch()"
							:error="errors['item' + itemIndex + 'styleCode']"
							:error-messages="errors['item' + itemIndex + 'styleCode']"
							:disabled="isLocked"
						></v-text-field>

						<div
							v-if="showItemSearchByLine == renderLineNumber(item)"
							class="search-input-dropdown"
							@keydown="(e) => keyboardShortcuts(item, e, itemIndex)"
							:data-line="renderLineNumber(item)"
						>
							<div class="search-input-dropdown-results">
								<ul
									@click.stop="preventCloseOnBlur = true"
									@mouseover="preventCloseOnBlur = true"
									@mouseleave="preventCloseOnBlur = false"
								>
									<li v-if="itemSearchResults.length == 0">No Results Found</li>
									<li
										v-for="(res, resIndex) in itemSearchResults"
										:key="resIndex"
										@mousedown="setItem(res, itemIndex)"
										tabindex="0"
										:class="{ 'focused-item': focusedItemIndex === resIndex }"
									>
										<SystemCategoryIcon :systemCategoryId="res.systemCategoryId" />
										<h4>{{ res.styleCode }}</h4>
										<p>{{ res.description }}</p>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-text-field
							outlined
							dense
							hide-details="auto"
							v-model="item.description"
							:error="errors['item' + itemIndex + 'description']"
							:error-messages="errors['item' + itemIndex + 'description']"
							disabled
						></v-text-field>
						<v-btn
							v-if="
								(item.note == '' || item.note == null) &&
								showItemNotesByLine !== renderLineNumber(item)
							"
							plain
							color="accent"
							class="item-table-line-subaction"
							@click="showItemNotesByLine = renderLineNumber(item)"
						>
							Add a note
						</v-btn>
						<div v-else class="item-table-notes">
							<template v-if="showItemNotesByLine == renderLineNumber(item)">
								<v-textarea outlined dense hide-details="auto" v-model="item.note"></v-textarea>
								<v-btn
									v-if="item.note && item.note.length > 0"
									class="ml-1"
									icon
									@click="showItemNotesByLine = null"
								>
									<v-icon>mdi-content-save</v-icon>
								</v-btn>
								<v-btn
									class="ml-1"
									icon
									@click="
										() => {
											item.note = '';
											showItemNotesByLine = null;
										}
									"
								>
									<v-icon>mdi-delete</v-icon>
								</v-btn>
							</template>
							<p
								v-else
								@click="showItemNotesByLine = renderLineNumber(item)"
								v-html="formatNewLines(item.note)"
							></p>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-text-field
							class="number-input"
							outlined
							dense
							hide-details="auto"
							v-model.number="item.quantity"
							type="number"
							min="0"
							:error="errors['item' + itemIndex + 'quantity']"
							:error-messages="errors['item' + itemIndex + 'quantity']"
							:disabled="isLocked"
						></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-btn
							v-if="
								(item.colorOptions && item.colorOptions.length > 0) ||
								$store.getters.requiresLettering(item.systemCategoryId)
							"
							class="item-table-line-action"
							:class="{ 'validation-error-button': hasColorError(itemIndex) }"
							outlined
							dense
							hide-details="auto"
							@click="
								showItemColorsByLine = renderLineNumber(item);
								showItemSizingByLine = null;
							"
							:disabled="isLocked"
						>
							<template v-if="validateColors(item)">
								<v-icon color="success">mdi-check-circle-outline</v-icon>
							</template>
							<template v-else> Need </template>
						</v-btn>
						<v-text-field v-else outlined dense hide-details="auto" disabled></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-btn
							v-if="item.sizeChart && item.sizeChart.sizes.length > 0"
							class="item-table-line-action"
							:class="{ 'validation-error-button': hasSizeError(itemIndex) }"
							outlined
							dense
							hide-details="auto"
							@click="
								showItemSizingByLine = renderLineNumber(item);
								showItemColorsByLine = null;
							"
							:disabled="isLocked"
						>
							<template v-if="validateSizing(item)">
								<v-icon color="success">mdi-check-circle-outline</v-icon>
							</template>
							<template v-else> Need </template>
						</v-btn>
						<v-text-field v-else outlined dense hide-details="auto" disabled></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<currency-text-field
							v-model="item.vendorCost"
							:class="{
								'validation-error': errors['item' + itemIndex + 'vendorCost'],
							}"
							:disabled="isLocked"
						/>

						<p v-if="errors['item' + itemIndex + 'vendorCost']" class="validation-error">
							{{ errors["item" + itemIndex + "vendorCost"] }}
						</p>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-text-field
							class="item-table-total"
							solo
							dense
							flat
							hide-details="auto"
							:value="renderItemTotal(item)"
							:readonly="isLocked"
						></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<v-btn class="mt-2" icon @click="removeSubitem(item)" :disabled="isLocked"
						><v-icon>mdi-close</v-icon></v-btn
					>
				</div>

				<!-- dropdown sizing and color rows -->
				<transition name="dropdown">
					<div
						v-if="showItemSizingByLine == renderLineNumber(item)"
						class="item-table-dropdown"
						:class="
							showItemSizingByLine == renderLineNumber(item) ? 'item-table-dropdown-active' : null
						"
						role="row-group"
					>
						<SizingChart
							v-if="item.sizeSelections"
							:showSizingChart="true"
							:modal="false"
							:sizes="sizes(item)"
							:sizeSelectionData="item.sizeSelections"
							:specialSizing="item.specialSizeSelections ? item.specialSizeSelections : []"
							:specialCharge="item.specialCharge ? item.specialCharge : 0"
							:includeLengths="item.sizeChart?.includeLengths ?? false"
							:maxQuantity="item.quantity"
							:enableInputs="true"
							@cancel="showItemSizingByLine = null"
							@save="updateItemSizing(item, $event)"
						/>
					</div>

					<div
						v-if="showItemColorsByLine == renderLineNumber(item)"
						class="item-table-dropdown"
						:class="
							showItemColorsByLine == renderLineNumber(item) ? 'item-table-dropdown-active' : null
						"
						role="row-group"
					>
						<ColorSelectionMenu
							:colorSelectionData="item.colorSelections"
							:textSelectionData="item.freeTextSelections"
							:modal="false"
							:colorOptions="colorOptions(item)"
							:type="item.systemCategoryId == 14 ? 'lettering' : ''"
							:letterPositionId="
								item.lettering && item.lettering.letterPositionId
									? item.lettering.letterPositionId
									: null
							"
							:customText="item.lettering && item.lettering.text ? item.lettering.text : null"
							:matchPreviousPoNumber="
								item.matchPreviousPoNumber ? item.matchPreviousPoNumber : null
							"
							:itemId="item.itemId"
							:requiresLettering="$store.getters.requiresLettering(item.systemCategoryId)"
							@cancel="showItemColorsByLine = null"
							@save="updateItemColors(item, $event)"
						/>
					</div>
				</transition>
			</div>
			<!-- package item -->
			<div
				v-for="(pkgItem, pkgIndex) in item.packageItems"
				:key="pkgIndex"
				class="flex-table-row"
				role="row-group"
			>
				<div class="flex-table-cell" role="cell">
					<span class="item-table-line-number">{{ renderLineNumber(pkgItem) }}</span>
				</div>
				<div class="flex-table-cell" role="cell">
					<v-icon class="item-table-level">mdi-subdirectory-arrow-right</v-icon>
					<SystemCategoryIcon :systemCategoryId="pkgItem.systemCategoryId" />
					<div class="form-input search-input">
						<v-text-field
							prepend-inner-icon="mdi-magnify"
							outlined
							dense
							hide-details="auto"
							v-model="pkgItem.styleCode"
							clearable
							@keyup="searchItems(pkgItem)"
							@blur="hideItemSearch()"
						></v-text-field>

						<div
							v-if="showItemSearchByLine == renderLineNumber(pkgItem)"
							class="search-input-dropdown"
						>
							<div class="search-input-dropdown-results">
								<ul>
									<li v-if="itemSearchResults.length == 0">No Results Found</li>
									<li
										v-for="(res, resIndex) in itemSearchResults"
										:key="resIndex"
										@click="setItem(res, itemIndex, pkgIndex)"
									>
										<SystemCategoryIcon :systemCategoryId="res.systemCategoryId" />
										<h4>{{ res.styleCode }}</h4>
										<p>{{ res.description }}</p>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-text-field
							outlined
							dense
							hide-details="auto"
							v-model="pkgItem.description"
						></v-text-field>
						<v-btn
							v-if="
								(pkgItem.note == '' || pkgItem.note == null) &&
								showItemNotesByLine !== renderLineNumber(pkgItem)
							"
							plain
							color="accent"
							class="item-table-line-subaction"
							@click="showItemNotesByLine = renderLineNumber(pkgItem)"
						>
							Add a note
						</v-btn>
						<div v-else class="item-table-notes">
							<template v-if="showItemNotesByLine == renderLineNumber(pkgItem)">
								<v-textarea outlined dense hide-details="auto" v-model="pkgItem.note"></v-textarea>
								<v-btn
									v-if="pkgItem.note && pkgItem.note.length > 0"
									class="ml-1"
									icon
									@click="showItemNotesByLine = null"
								>
									<v-icon>mdi-content-save</v-icon>
								</v-btn>
								<v-btn
									class="ml-1"
									icon
									@click="
										() => {
											pkgItem.note = '';
											showItemNotesByLine = null;
										}
									"
								>
									<v-icon>mdi-delete</v-icon>
								</v-btn>
							</template>
							<p
								v-else
								@click="showItemNotesByLine = renderLineNumber(pkgItem)"
								v-html="formatNewLines(pkgItem.note)"
							></p>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-text-field
							class="number-input"
							outlined
							dense
							hide-details="auto"
							v-model.number="pkgItem.quantity"
							type="number"
							min="0"
						></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						{{ pkg.includeColors ? "N/A" : "YES" }}
						<v-btn
							v-if="pkgItem.includeColors"
							class="item-table-line-action"
							outlined
							dense
							hide-details="auto"
							@click="
								showItemColorsByLine = renderLineNumber(pkgItem);
								showItemSizingByLine = null;
							"
						>
							<template
								v-if="
									pkgItem.colorOptions && pkgItem.colorOptions.length > 0
										? pkgItem.colorOptions[0].selectedColor
										: false
								"
							>
								<v-icon color="success">mdi-check-circle-outline dsds</v-icon>
							</template>
							<template v-else> Need </template>
						</v-btn>
						<v-text-field v-else outlined dense hide-details="auto" disabled></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input" v-if="pkgItem.includeSizingChart">
						<v-btn
							v-if="pkgItem.includeSizingChart"
							class="item-table-line-action"
							outlined
							dense
							hide-details="auto"
							@click="
								showItemSizingByLine = renderLineNumber(pkgItem);
								showItemColorsByLine = null;
							"
						>
							<template v-if="pkgItem.sizeChart[0].quantity.length > 0">
								<v-icon color="success">mdi-check-circle-outline</v-icon>
							</template>
							<template v-else> Need </template>
						</v-btn>
						<v-text-field v-else outlined dense hide-details="auto" disabled></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<currency-text-field v-model="pkgItem.cost" />
						<p v-if="pkgItem.specialCharge" class="item-table-line-upcharge">
							+ {{ renderPrice(pkgItem.specialCharge) }}
						</p>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="form-input">
						<v-text-field
							class="item-table-total"
							solo
							dense
							flat
							hide-details="auto"
							:value="renderItemTotal(pkgItem, item)"
						></v-text-field>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<v-btn class="mt-2" icon @click="removeSubitem(pkgItem, item)"
						><v-icon>mdi-close</v-icon></v-btn
					>
				</div>

				<!-- dropdown sizing and color rows -->
				<transition name="dropdown">
					<div
						v-if="showItemSizingByLine == renderLineNumber(pkgItem)"
						class="item-table-dropdown"
						:class="
							showItemSizingByLine == renderLineNumber(pkgItem)
								? 'item-table-dropdown-active'
								: null
						"
						role="row-group"
					>
						<SizingChart
							:showSizingChart="true"
							:modal="false"
							:sizes="pkgItem.sizeChart"
							:specialSizing="pkgItem.specialSizes ? pkgItem.specialSizes : []"
							:includeSpecialCharge="false"
							:includeLengths="pkgItem.includeLengths"
							:enableInputs="true"
							@cancel="showItemSizingByLine = null"
							@save="updateItemSizing(pkgItem, $event)"
						/>
					</div>

					<div
						v-if="showItemColorsByLine == renderLineNumber(pkgItem)"
						class="item-table-dropdown"
						:class="
							showItemColorsByLine == renderLineNumber(pkgItem)
								? 'item-table-dropdown-active'
								: null
						"
						role="row-group"
					>
						<ColorSelectionMenu
							:modal="false"
							:colorOptions="colorOptions(pkgItem)"
							:textSelectionData="pkgItem.freeTextSelections"
							:type="pkgItem.systemCategoryId == 14 ? 'lettering' : ''"
							:letterPositionId="pkgItem.letterPositionId ? pkgItem.letterPositionId : null"
							:customLetters="pkgItem.customLetters ? pkgItem.customLetters : null"
							:matchPreviousPoNumber="
								pkgItem.matchPreviousPoNumber ? pkgItem.matchPreviousPoNumber : null
							"
							:itemId="pkgItem.itemId"
							:requiresLettering="$store.getters.requiresLettering(pkgItem.systemCategoryId)"
							@cancel="showItemColorsByLine = null"
							@save="updateItemColors(pkgItem, $event)"
						/>
					</div>
				</transition>

				<!-- associated sub item -->
				<div
					v-for="(subItem, subIndex) in pkgItem.subItems"
					:key="subIndex"
					class="flex-table-row"
					role="row-group"
				>
					<div class="flex-table-cell" role="cell">
						<span class="item-table-line-number">{{ renderLineNumber(subItem) }}</span>
					</div>
					<div class="flex-table-cell" role="cell">
						<v-icon class="item-table-level">&nbsp;</v-icon>
						<v-icon class="item-table-level">mdi-subdirectory-arrow-right</v-icon>
						<SystemCategoryIcon :systemCategoryId="subItem.systemCategoryId" />
						<div class="form-input search-input">
							<v-text-field
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details="auto"
								v-model="subItem.styleCode"
								clearable
								@keyup="searchItems(subItem)"
								@blur="hideItemSearch()"
							></v-text-field>

							<div
								v-if="showItemSearchByLine == renderLineNumber(subItem)"
								class="search-input-dropdown"
							>
								<div class="search-input-dropdown-results">
									<ul>
										<li v-if="itemSearchResults.length == 0">No Results Found</li>
										<li
											v-for="(res, resIndex) in itemSearchResults"
											:key="resIndex"
											@click="setItem(res, itemIndex, pkgIndex, subIndex)"
										>
											<SystemCategoryIcon :systemCategoryId="res.systemCategoryId" />
											<h4>{{ res.styleCode }}</h4>
											<p>{{ res.description }}</p>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
					<div class="flex-table-cell" role="cell">
						<div class="form-input">
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="subItem.description"
							></v-text-field>
							<v-btn
								v-if="
									(subItem.note == '' || subItem.note == null) &&
									showItemNotesByLine !== renderLineNumber(subItem)
								"
								plain
								color="accent"
								class="item-table-line-subaction"
								@click="showItemNotesByLine = renderLineNumber(subItem)"
							>
								Add a note
							</v-btn>
							<div v-else class="item-table-notes">
								<template v-if="showItemNotesByLine == renderLineNumber(subItem)">
									<v-textarea
										outlined
										dense
										hide-details="auto"
										v-model="subItem.note"
									></v-textarea>
									<v-btn
										v-if="subItem.note && subItem.note.length > 0"
										class="ml-1"
										icon
										@click="showItemNotesByLine = null"
									>
										<v-icon>mdi-content-save</v-icon>
									</v-btn>
									<v-btn
										class="ml-1"
										icon
										@click="
											() => {
												subItem.note = '';
												showItemNotesByLine = null;
											}
										"
									>
										<v-icon>mdi-delete</v-icon>
									</v-btn>
								</template>
								<p
									v-else
									@click="showItemNotesByLine = renderLineNumber(subItem)"
									v-html="formatNewLines(subItem.note)"
								></p>
							</div>
						</div>
					</div>
					<div class="flex-table-cell" role="cell">
						<div class="form-input">
							<v-text-field
								class="number-input"
								outlined
								dense
								hide-details="auto"
								v-model.number="subItem.quantity"
								type="number"
								min="0"
							></v-text-field>
						</div>
					</div>
					<div class="flex-table-cell" role="cell">
						<div class="form-input">
							<v-btn
								v-if="subItem.includeColors"
								class="item-table-line-action"
								outlined
								dense
								hide-details="auto"
								@click="
									showItemColorsByLine = renderLineNumber(subItem);
									showItemSizingByLine = null;
								"
							>
								<template v-if="subItem.colorOptions[0].selectedColor">
									<v-icon color="success">mdi-check-circle-outline</v-icon>
								</template>
								<template v-else> Need </template>
							</v-btn>
							<v-text-field v-else outlined dense hide-details="auto" disabled></v-text-field>
						</div>
					</div>
					<div class="flex-table-cell" role="cell">
						<div class="form-input" v-if="subItem.includeSizingChart">
							<v-btn
								v-if="subItem.includeSizingChart"
								class="item-table-line-action"
								outlined
								dense
								hide-details="auto"
								@click="
									showItemSizingByLine = renderLineNumber(subItem);
									showItemColorsByLine = null;
								"
							>
								<template v-if="subItem.sizeChart[0].quantity.length > 0">
									<v-icon color="success">mdi-check-circle-outline</v-icon>
								</template>
								<template v-else> Need </template>
							</v-btn>
							<v-text-field v-else outlined dense hide-details="auto" disabled></v-text-field>
						</div>
					</div>
					<div class="flex-table-cell" role="cell">
						<div class="form-input">
							<currency-text-field v-model="subItem.cost" />
							<p v-if="subItem.specialCharge" class="item-table-line-upcharge">
								+ {{ renderPrice(subItem.specialCharge) }}
							</p>
						</div>
					</div>
					<div class="flex-table-cell" role="cell">
						<div class="form-input">
							<v-text-field
								class="item-table-total"
								solo
								dense
								flat
								hide-details="auto"
								:value="renderItemTotal(subItem, item)"
							></v-text-field>
						</div>
					</div>
					<div class="flex-table-cell" role="cell">
						<v-btn class="mt-2" icon @click="removeSubitem(subItem, pkgItem)"
							><v-icon>mdi-close</v-icon></v-btn
						>
					</div>

					<!-- dropdown sizing and color rows -->
					<transition name="dropdown">
						<div
							v-if="showItemSizingByLine == renderLineNumber(subItem)"
							class="item-table-dropdown"
							:class="
								showItemSizingByLine == renderLineNumber(subItem)
									? 'item-table-dropdown-active'
									: null
							"
							role="row-group"
						>
							<SizingChart
								:showSizingChart="true"
								:modal="false"
								:sizes="subItem.sizeChart"
								:specialSizing="subItem.specialSizes ? subItem.specialSizes : []"
								:includeSpecialCharge="false"
								:includeLengths="subItem.includeLengths"
								:enableInputs="true"
								@cancel="showItemSizingByLine = null"
								@save="updateItemSizing(subItem, $event)"
							/>
						</div>

						<div
							v-if="showItemColorsByLine == renderLineNumber(subItem)"
							class="item-table-dropdown"
							:class="
								showItemColorsByLine == renderLineNumber(subItem)
									? 'item-table-dropdown-active'
									: null
							"
							role="row-group"
						>
							<ColorSelectionMenu
								:modal="false"
								:colorOptions="colorOptions(subItem)"
								:textSelectionData="subItem.freeTextSelections"
								:type="subItem.systemCategoryId == 14 ? 'lettering' : ''"
								:letterPositionId="subItem.letterPositionId ? subItem.letterPositionId : null"
								:customLetters="subItem.customLetters ? subItem.customLetters : null"
								:matchPreviousPoNumber="
									subItem.matchPreviousPoNumber ? subItem.matchPreviousPoNumber : null
								"
								:itemId="subItem.itemId"
								:requiresLettering="$store.getters.requiresLettering(subItem.systemCategoryId)"
								@cancel="showItemColorsByLine = null"
								@save="updateItemColors(subItem, $event)"
							/>
						</div>
					</transition>
				</div>
			</div>
		</div>

		<div class="item-table-action">
			<v-btn plain color="accent" @click="addMainItem()" :disabled="isLocked">
				<v-icon>mdi-plus</v-icon>
				Add Another Item
			</v-btn>
		</div>

		<p class="validation-error" v-if="errors.items && errors.items !== ''">{{ errors.items }}</p>
	</div>
</template>

<script>
import gql from "graphql-tag";
import SizingChart from "@/components/SizingChart.vue";
import ColorSelectionMenu from "@/components/ColorSelectionMenu.vue";
import systemCategories from "../../helpers/systemCategories";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import { validateSizing } from "@/helpers/validator";
import { hasValidColorSelections } from "@/DataTypes";
import { GET_ANY_ITEM_BY_ID } from "@/gql/inventory";
import { renderPrice } from "@/helpers/PricingCalculator";
export default {
	name: "PurchaseOrderEntryTable",
	props: {
		update: {
			type: Boolean,
			default: false,
		},
		isLocked: {
			type: Boolean,
			default: false,
		},
		tableData: {
			type: Array,
			default() {
				return [];
			},
		},
		errors: {
			type: Object,
			default() {
				return [];
			},
		},
	},
	components: {
		SizingChart,
		ColorSelectionMenu,
		SystemCategoryIcon,
	},
	data() {
		return {
			newTableItem: null,
			localTableData: [],
			showItemSearchByLine: null,
			itemSearchResults: [],
			showItemNotesByLine: null,
			showItemColorsByLine: null,
			showItemSizingByLine: null,
			categories: [],
			// Keyboard navigation state
			focusedItemIndex: -1,
			preventCloseOnBlur: false,
			hideSearchTimeout: null,
			currentItemIndex: null,
		};
	},
	async mounted() {
		this.localTableData = JSON.parse(JSON.stringify(this.tableData));

		const tableData = [];

		for (const entry of this.tableData) {
			const item = await this.getInventoryItem(entry.inventoryItemId);
			const itemWithOptions = { ...entry };
			if (item) {
				itemWithOptions.colorOptions = item.colorOptions || [];
				itemWithOptions.sizeChart = item.sizeChart;
			}
			tableData.push(itemWithOptions);
		}

		this.localTableData = JSON.parse(JSON.stringify(tableData));
	},
	watch: {
		update: {
			handler() {
				console.log("updating table", this.tableData);
				this.localTableData = this.tableData;
			},
			immediate: true,
		},
		tableData: {
			handler() {
				console.log(this.tableData);
				console.log("updating table", this.tableData);
				this.localTableData = this.tableData;
			},
			immediate: true,
		},
		localTableData: {
			handler(prev) {
				console.log("updating table-local", this.tableData, prev);
				if (this.tableData !== this.localTableData) {
					console.log("update to table detected", this.localTableData, this.newTableItem);
					//if new item was added send the data to parent along with updated table items
					let data = {
						tableItems: this.localTableData,
						newTableItem: this.newTableItem,
					};
					this.$emit("updatedTableData", data);

					if (this.newTableItem) {
						//reset the state of the new added item
						this.newTableItem = null;
					}
				}
			},
			deep: true,
		},
	},
	methods: {
		validateSizing,

		// Validation error checking methods for button styling
		hasColorError(itemIndex) {
			return !!this.errors[`item${itemIndex}color`];
		},

		hasSizeError(itemIndex) {
			return !!this.errors[`item${itemIndex}size`];
		},

		renderLineNumber(itemObj) {
			//find the matching obj by index in the computedItemTable and return its line number (index + 1)
			let lineNumber = this.computedItemsTable.findIndex((obj) => obj == itemObj);

			return lineNumber >= 0 ? lineNumber + 1 : "#";
		},

		renderPrice,

		renderItemTotal(item, mainParentItem) {
			let formatter = new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
			});

			let vendorCost = item.vendorCost ? parseFloat(item.vendorCost) : 0;
			let itemCost = item.cost ? parseFloat(item.cost) : 0;
			let quantity = item.quantity ? parseFloat(item.quantity) : 0;

			let itemTotal = 0;

			itemTotal += (itemCost + vendorCost) * quantity;
			return formatter.format(itemTotal.toFixed(2));
		},

		colorOptions(item) {
			// Safe handling of colorOptions - return empty array if undefined/null
			if (!Array.isArray(item?.colorOptions)) {
				return [];
			}
			return item.colorOptions.slice().sort((a, b) => {
				const positionA = typeof a.position === "number" ? a.position : Infinity;
				const positionB = typeof b.position === "number" ? b.position : Infinity;
				return positionA - positionB;
			});
		},

		addMainItem() {
			//reset new table item state
			this.newTableItem = null;

			let newItem = {
				systemCategoryId: null,
				styleCode: "",
				description: "",
				price: 0.0,
				cost: 0.0,
				quantity: "1",
				colors: "",
				size: "",
				total: "",
				dropShip: false,
				notes: "",
				packageItems: [],
			};

			this.localTableData.push(newItem);

			// Focus the style code input for the newly added item
			this.$nextTick(() => {
				const lineNumber = this.computedItemsTable.length;
				const inputElement = this.$el.querySelector(`[role="row-group"]:last-of-type input`);
				if (inputElement) {
					inputElement.focus();
				}
			});
		},

		removeSubitem(childItem, parentItem) {
			//reset newTableItem state
			this.newTableItem = null;

			//if no parent item then remove the childItem from main data otherwise if parent has packageItems remove the child package item otherwise remove the child subitem
			if (!parentItem) {
				this.localTableData = this.localTableData.filter((obj) => obj !== childItem);
			} else if (parentItem.packageItems) {
				parentItem.packageItems = parentItem.packageItems.filter((obj) => obj !== childItem);
			} else {
				parentItem.subItems = parentItem.subItems.filter((obj) => obj !== childItem);
			}
		},

		hideItemSearch() {
			// Clear any pending timeout to prevent race conditions
			if (this.hideSearchTimeout !== null) {
				clearTimeout(this.hideSearchTimeout);
			}

			if (this.preventCloseOnBlur) {
				// Don't close the dropdown - just reset the flag after a delay
				// This allows keyboard navigation and clicking on dropdown items
				this.hideSearchTimeout = setTimeout(() => {
					this.preventCloseOnBlur = false;
					this.hideSearchTimeout = null;
				}, 200);
			} else {
				// Close the dropdown
				this.hideSearchTimeout = setTimeout(() => {
					this.showItemSearchByLine = null;
					this.hideSearchTimeout = null;
				}, 150);
			}
		},

		keyboardShortcuts(item, e, itemIndex) {
			const keys = { up: 38, down: 40, right: 39, tab: 9, enter: 13 };

			// Check if the keyCode is a navigation/action key
			if (!Object.values(keys).includes(e.keyCode)) {
				return;
			}

			// Store context for selectItem
			this.currentItemIndex = itemIndex;

			const visibleResults = this.itemSearchResults;

			// Auto-select if single visible result and Enter/Tab pressed
			if (
				(e.keyCode === keys.enter || e.keyCode === keys.tab) &&
				visibleResults.length === 1
			) {
				// Check if Tab key would select the same item (no actual change)
				const selectedResult = visibleResults[0];
				const currentItemId = item.inventoryItemId || item.itemId;
				const selectedItemId = selectedResult.inventoryItemId;

				// If selecting the exact same item, prevent default and skip selection
				if (currentItemId === selectedItemId && currentItemId !== undefined) {
					e.preventDefault();
					// Just move focus away from dropdown (blur)
					if (e.target && typeof e.target.blur === 'function') {
						e.target.blur();
					}
					return;
				}

				e.preventDefault();
				this.selectItem(0);
				return;
			}

			// Auto-select first result if Enter pressed with multiple results and no item focused
			if (
				e.keyCode === keys.enter &&
				visibleResults.length > 1 &&
				this.focusedItemIndex < 0
			) {
				e.preventDefault();
				this.selectItem(0);
				return;
			}

			if (e.keyCode === keys.up || e.keyCode === keys.down) {
				e.preventDefault();
				this.preventCloseOnBlur = true;
				this.moveFocus(e.keyCode === keys.up ? "up" : "down");
			} else if (e.keyCode === keys.right || e.keyCode === keys.tab || e.keyCode === keys.enter) {
				if (this.focusedItemIndex >= 0) {
					e.preventDefault();
					this.selectItem(this.focusedItemIndex);
				}
			}
		},

		moveFocus(direction) {
			const visibleResults = this.itemSearchResults;
			const visibleLength = visibleResults.length;

			if (visibleLength === 0) return;

			// Calculate new index
			let newIndex;
			if (direction === "up") {
				newIndex = this.focusedItemIndex <= 0 ? visibleLength - 1 : this.focusedItemIndex - 1;
			} else {
				newIndex = this.focusedItemIndex >= visibleLength - 1 ? 0 : this.focusedItemIndex + 1;
			}

			this.focusedItemIndex = newIndex;

			// Wait for the DOM to update, then focus the new item
			this.$nextTick(() => {
				// Scope selection to only the currently active dropdown
				const activeDropdown = this.$el.querySelector(
					`[data-line="${this.showItemSearchByLine}"] .search-input-dropdown-results`
				);
				if (!activeDropdown) return;

				// Query only visible li elements
				let listItems = Array.from(activeDropdown.querySelectorAll("li")).filter(
					(li) => li.offsetParent !== null
				);
				let newFocusedElement = listItems[newIndex];
				if (newFocusedElement) {
					newFocusedElement.focus();
				}
			});
		},

		selectItem(index) {
			const item = this.itemSearchResults[index];
			if (item) {
				this.setItem(item, this.currentItemIndex);
				this.preventCloseOnBlur = false;
			}
		},

		async setItem(resultItem, parentIndex, subIndex, subIndex2, type) {
			let selectedItem = {};
			this.$emit("clearErrors");
			this.showItemSearchByLine = null;

			if (!resultItem || !resultItem.inventoryItemId) return;

			selectedItem = await this.getInventoryItem(resultItem.inventoryItemId);

			//check what level we are updating there are only 3 levels
			if (parentIndex !== undefined) {
				//update top level items with the selected search result item
				console.log("updating level 1 item with result item", selectedItem);

				if (!selectedItem.quantity) {
					selectedItem.quantity = 1;
				}

				selectedItem.sizeSelections = [];

				// selectedItem.type = type;

				this.localTableData.splice(parentIndex, 1, selectedItem);
			}

			//reset the search display
			this.showItemSearchByLine = null;
		},

		async getInventoryItem(itemId) {
			let response = {};

			try {
				//TODO need to add processTypeId to query save and retreive
				const res = await this.$apollo.query({
					query: GET_ANY_ITEM_BY_ID,
					variables: {
						id: itemId,
					},
					fetchPolicy: "no-cache",
				});

				console.log("getting inventory item", res);
				return res.data.inventoryItem;
			} catch (error) {
				console.log(error);
			}

			return response;
		},

		async searchItems(item) {
			try {
				let res;

				const SEARCH_INVENTORY_LISTINGS_BY_TYPE = gql`
					query SearchInventoryListing(
						$searchTerm: String = null
						$offset: Int! = 0
						$take: Int! = 20
					) {
						inventoryListings(
							searchTerm: $searchTerm
							skip: $offset
							take: $take
							where: {
								inactiveDate: { eq: null }
								type: { or: [{ eq: "item" }, { eq: "subItem" }] }
							}
						) {
							items {
								inventoryItemId
								systemCategoryId
								styleCode
								description
								type
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;

				res = await this.$apollo.query({
					query: SEARCH_INVENTORY_LISTINGS_BY_TYPE,
					fetchPolicy: "no-cache",
					variables: {
						searchTerm: item.styleCode,
					},
				});

				let results = res.data.inventoryListings.items;

				this.itemSearchResults = results;

				this.showItemSearchByLine = this.renderLineNumber(item);
			} catch (error) {
				console.log(error);
			}
		},

		updateItemColors(workingItem, colorObj) {
			console.log(workingItem, colorObj);
			//check if workingItem is a lettering category and update its letterPosition and lettering
			if (workingItem.systemCategoryId == 14) {
				workingItem.colorSelections = colorObj.colorSelections;
				workingItem.lettering = {
					letterPositionId: colorObj.letterPositionId,
					text: colorObj.customText,
				};
			} else if (colorObj.matchPreviousPoNumber) {
				workingItem.colorSelections = [];
			} else {
				//update the workingItems colorOptions
				workingItem.colorSelections = colorObj.colorSelections;
			}

			workingItem.matchPreviousPoNumber = colorObj.matchPreviousPoNumber;
			//reset color dropdown state
			this.showItemColorsByLine = null;
			this.$emit("clearErrors");
		},

		validateColors(workingItem) {
			return hasValidColorSelections(
				workingItem,
				this.$store.getters.requiresLettering(workingItem.systemCategoryId)
			);
		},

		updateItemSizing(workingItem, sizeObj) {
			console.log("updating item sizing", workingItem, sizeObj);
			//update the workingItems sizing data
			workingItem.sizeSelections = sizeObj.sizeSelections;
			workingItem.specialSizeSelections = sizeObj.specialSizeSelections;
			workingItem.specialCharge = sizeObj.specialCharge;
			// Only update sizes array, preserving other sizeChart properties (includeLengths, name, etc.)
			if (workingItem.sizeChart && sizeObj.sizeChart) {
				workingItem.sizeChart.sizes = sizeObj.sizeChart.sizes;
			}

			console.log(workingItem);

			//reset size dropdown state
			this.showItemSizingByLine = null;

			this.$emit("clearErrors");
		},
		sizes(item) {
			return JSON.parse(JSON.stringify(item.sizeChart.sizes));
		},
		formatNewLines(text) {
			if (!text) return "";
			return text.replace(/\n/g, "<br/>").replace(/\t/g, "&emsp;");
		},
	},
	computed: {
		computedItemsTable() {
			let itemsArr = [];

			if (this.localTableData && this.localTableData.length > 0) {
				//loop through all level 1 items and add them to the itemsArr and check for package items
				this.localTableData.forEach((item) => {
					itemsArr.push(item);

					if (item.packageItems && item.packageItems.length > 0) {
						//loop through all level 2 items and add them to the itemsArr and check for sub items
						item.packageItems.forEach((pkgItem) => {
							itemsArr.push(pkgItem);

							if (pkgItem.subItems && pkgItem.subItems.length > 0) {
								//loop through all level 3 items and add them to the itemsArr
								pkgItem.subItems.forEach((sub) => {
									itemsArr.push(sub);
								});
							}
						});
					}
				});
			}

			return itemsArr;
		},
	},
};
</script>

<style lang="scss" scoped>
.item-table {
	position: relative;

	&-title {
		font-size: 1.4rem;
	}

	.flex-table[role="table-body"] {
		background-color: $color-white;
		border-top: none;
		border-bottom: none;
	}

	.flex-table-cell {
		align-items: flex-start;

		&:nth-child(1) {
			width: 3%;
			justify-content: center;
		}
		&:nth-child(2) {
			width: 24%;
		}
		&:nth-child(3) {
			width: 34%;
		}
		&:nth-child(4) {
			width: 7%;
			justify-content: center;
		}
		&:nth-child(5) {
			width: 5%;
			justify-content: center;
		}
		&:nth-child(6) {
			width: 5%;
			justify-content: center;
		}
		&:nth-child(7) {
			width: 10%;
		}
		&:nth-child(8) {
			width: 9%;
		}
		&:nth-child(9) {
			width: 3%;
			justify-content: center;
		}
	}

	.form-input {
		position: relative;
		margin: 0;
		width: 100%;
		flex-wrap: wrap;

		& > .v-input {
			width: 100%;
			margin-top: 0.8rem;
		}

		// Target currency-text-field's nested v-input (wrapped in v-tooltip)
		// Use ::v-deep to pierce scoped styles into child component
		&::v-deep .price-input {
			width: 100%;
			margin-top: 0.8rem;
		}
	}

	& &-line {
		&-number {
			margin-top: 1.4rem;
		}

		&-action {
			min-width: initial;
			width: 100%;
			margin-top: 0.8rem;
			border-color: $color-gray;
			color: $color-primary;
		}

		&-subaction {
			display: block;
			padding: 0.5rem;
			margin-top: 0.25rem;
			height: initial;
			font-size: 1rem;
		}

		&-upcharge {
			color: $color-primary;
			padding-top: 0.25rem;
		}
	}

	&-level {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		border: 1px solid transparent;
		border-radius: 5px;
		min-height: 5rem;
		min-width: 5rem;
		margin-right: 1rem;
	}

	&-action {
		background-color: $color-white;
		display: block;
		padding: 0.8rem;
		border: 1px solid $color-gray;

		// Improve keyboard focus visibility for the "Add Another Item" button
		.v-btn:focus {
			background-color: rgba(0, 123, 255, 0.1);
			outline: 2px solid $color-primary;
			outline-offset: 2px;
		}
	}

	&-notes {
		display: flex;
		align-items: center;
		width: 100%;
		margin-top: 0.4rem;
		font-size: 1.2rem;

		p {
			padding: 0 0.5rem;

			&:hover {
				cursor: pointer;
			}
		}

		.v-text-field {
			margin-top: initial;
			background-color: $color-gray-light-2;
		}
	}

	&-dropdown {
		flex-grow: 1;
		position: relative;
		max-height: 0;
		min-height: 0;
		border: none;
		overflow: hidden;
		transition: all 0.3s ease-in-out;

		&-active {
			max-height: 650rem;
		}
	}

	&-total {
		display: flex;
		align-items: center;
		font-size: 1.4rem;
	}
}

.search-input {
	&-dropdown {
		position: absolute;
		left: 0;
		bottom: -28.5rem; //height of the filter + extra spacing
		background-color: $color-white;
		border-radius: 3px;
		width: 40vw;
		max-width: 40vw;
		height: 28rem;
		box-shadow: rgba(50, 50, 93, 0.15) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
		z-index: 1000;
		overflow-y: auto;
		overflow-x: hidden;

		&-results {
			ul {
				list-style: none;
				padding-left: 0;

				li {
					display: flex;
					flex-flow: row wrap;
					align-items: center;
					font-size: 1.3rem;
					white-space: nowrap;
					transition: all 0.4s;
					padding: 0.8rem;
					padding-left: 1rem;

					&:hover {
						cursor: pointer;
						background-color: $color-gray-light;
					}

					&:focus,
					&.focused-item {
						background-color: $color-gray-light;
						outline: none;
					}

					&:not(:last-child) {
						border-bottom: 1px solid $color-gray;
					}

					h4,
					p {
						font-size: 1.4rem;
						font-weight: normal;
						text-transform: uppercase;
					}

					h4 {
						margin: 0 1.5rem 0 1rem;
					}
				}
			}
		}
	}
}

.dropdown-enter-active,
.dropdown-leave-active {
	transition: max-height 0.3s ease-in-out;
}

.dropdown-enter, .dropdown-leave-to /* .dropdown-leave-active below version 2.1.8 */ {
	max-height: 0;
}

.validation-error {
	color: $color-red;
	font-size: 1.2rem;
}

.validation-error.v-input {
	outline: 2px solid $color-red !important;
	border: 0px !important;

	.v-input__control {
		border: 0px !important;
	}
}

/* ===== Enhanced Validation Error Styling ===== */

/* Button-level validation error (for COLORS and SIZE buttons) */
.validation-error-button {
	border: 2px solid #d32f2f !important;
	background-color: #ffebee !important;
	animation: pulse-validation-error 2s ease-in-out 3;

	&:hover {
		background-color: #ffcdd2 !important;
	}
}

/* Pulsing animation for validation errors */
@keyframes pulse-validation-error {
	0%,
	100% {
		background-color: #ffebee;
		border-color: #d32f2f;
	}
	50% {
		background-color: #ffcdd2;
		border-color: #c62828;
	}
}
</style>
